import { Eye, EyeOff, LoaderCircle } from 'lucide-solid';
import { createSignal, Match, Show, Switch, type JSX } from 'solid-js';
import { useVenue } from '../state/VenueProvider';
import { TEST_IDS } from '../testIds';

/**
 * The one card standing between a browser and the console: sign in, create an account, or ask for a
 * password reset.
 *
 * All three are the same card in different modes rather than separate screens, because they are the same
 * moment — someone at the door with an email address. Two of them end in a state the card shows instead
 * of a form (an account waiting for its address to be proven, a reset email on its way), and those
 * states are the whole screen: there is nothing else to do from here until the inbox is opened.
 *
 * The verification hold is not cosmetic. An account whose address is unproven is signed in to Firebase
 * but the cloud will not grant it a membership or let it claim an invite, so letting it into the console
 * would show an empty room and no reason why. `awaitingVerification` outranks every mode below.
 */
type Mode = 'signin' | 'create' | 'reset' | 'reset-sent';

export function SignIn(): JSX.Element {
  const venue = useVenue();
  const [mode, setMode] = createSignal<Mode>('signin');
  const [name, setName] = createSignal('');
  const [email, setEmail] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [visible, setVisible] = createSignal(false);

  /** The password rule, in one place: the sign-in button, the create button and the input all read it. */
  const passwordOk = (): boolean => password().length >= 6;
  const emailOk = (): boolean => email().includes('@');

  function go(next: Mode): void {
    venue.clearAuthError();
    setMode(next);
  }

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (mode() === 'create') {
      await venue.createAccount(name(), email(), password()).catch(() => undefined);
      return;
    }
    if (mode() === 'reset') {
      // Resolving is the only signal there is: with email enumeration protection on, an address with no
      // account resolves exactly like one that has an account. A refusal throws and stays on the form.
      await venue
        .sendPasswordReset(email())
        .then(() => setMode('reset-sent'))
        .catch(() => undefined);
      return;
    }
    await venue.signInEmail(email(), password()).catch(() => undefined);
  }

  return (
    <main class="signin-shell">
      <section class="signin-card" aria-labelledby="signin-title">
        <div class="brand-lockup brand-lockup-large">
          <img src="/brand/nuance-logo-256.png" alt="" />
          <span>Nuance Venue</span>
        </div>
        <Show
          when={!venue.awaitingVerification()}
          fallback={
            <div class="signin-form">
              <header>
                <h1 id="signin-title">Check your email</h1>
              </header>
              <Show when={venue.authError()}><p class="form-error" role="alert">{venue.authError()}</p></Show>
              <button
                class="button button-secondary button-large"
                type="button"
                disabled={venue.authBusy()}
                onClick={() => void venue.resendVerification().catch(() => undefined)}
                data-testid={TEST_IDS.resendVerification}
              >
                <Show when={venue.authBusy()}><LoaderCircle class="spin" size={18} /></Show>
                Resend
              </button>
              <div class="signin-alt signin-alt-single">
                <button type="button" onClick={() => void venue.signOut()} data-testid={TEST_IDS.backToSignIn}>Back to sign in</button>
              </div>
            </div>
          }
        >
          <Show
            when={mode() !== 'reset-sent'}
            fallback={
              <div class="signin-form">
                <header>
                  <h1 id="signin-title">Check your email</h1>
                </header>
                <div class="signin-alt signin-alt-single">
                  <button type="button" onClick={() => go('signin')} data-testid={TEST_IDS.backToSignIn}>Back to sign in</button>
                </div>
              </div>
            }
          >
            <form class="signin-form" onSubmit={submit}>
              <header>
                <h1 id="signin-title">
                  <Switch fallback="Sign in">
                    <Match when={mode() === 'create'}>Create account</Match>
                    <Match when={mode() === 'reset'}>Reset password</Match>
                  </Switch>
                </h1>
              </header>
              <Show when={mode() === 'create'}>
                <label>
                  <span>Name</span>
                  <input
                    type="text"
                    name="name"
                    autocomplete="name"
                    required
                    value={name()}
                    onInput={(event) => setName(event.currentTarget.value)}
                    data-testid={TEST_IDS.name}
                  />
                </label>
              </Show>
              <label>
                <span>Email</span>
                <input
                  type="email"
                  name="email"
                  autocomplete="email"
                  required
                  value={email()}
                  onInput={(event) => setEmail(event.currentTarget.value)}
                  data-testid={TEST_IDS.email}
                />
              </label>
              <Show when={mode() !== 'reset'}>
                <label>
                  <span>Password</span>
                  <div class="password-field">
                    <input
                      type={visible() ? 'text' : 'password'}
                      name="password"
                      autocomplete={mode() === 'create' ? 'new-password' : 'current-password'}
                      minlength="6"
                      required
                      value={password()}
                      onInput={(event) => setPassword(event.currentTarget.value)}
                      data-testid={TEST_IDS.password}
                    />
                    <button type="button" aria-label={visible() ? 'Hide password' : 'Show password'} onClick={() => setVisible((value) => !value)}>
                      {visible() ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </label>
              </Show>
              <Show when={venue.authError()}><p class="form-error" role="alert">{venue.authError()}</p></Show>
              <Switch>
                <Match when={mode() === 'create'}>
                  <button class="button button-primary button-large" type="submit" disabled={venue.authBusy() || !name().trim() || !emailOk() || !passwordOk()} data-testid={TEST_IDS.createAccount}>
                    <Show when={venue.authBusy()}><LoaderCircle class="spin" size={18} /></Show>
                    Create account
                  </button>
                </Match>
                <Match when={mode() === 'reset'}>
                  <button class="button button-primary button-large" type="submit" disabled={venue.authBusy() || !emailOk()} data-testid={TEST_IDS.sendReset}>
                    <Show when={venue.authBusy()}><LoaderCircle class="spin" size={18} /></Show>
                    Send reset email
                  </button>
                </Match>
                <Match when={mode() === 'signin'}>
                  <button class="button button-primary button-large" type="submit" disabled={venue.authBusy() || !emailOk() || !passwordOk()} data-testid={TEST_IDS.signIn}>
                    <Show when={venue.authBusy()}><LoaderCircle class="spin" size={18} /></Show>
                    Sign in
                  </button>
                </Match>
              </Switch>
              <Show when={mode() === 'signin'}>
                <div class="form-divider"><span>or</span></div>
                <button class="button button-secondary button-large" type="button" disabled={venue.authBusy()} onClick={() => void venue.signInGoogle().catch(() => undefined)}>
                  <span class="google-mark" aria-hidden="true">G</span> Continue with Google
                </button>
              </Show>
              <div class="signin-alt" classList={{ 'signin-alt-single': mode() !== 'signin' }}>
                <Switch>
                  <Match when={mode() === 'signin'}>
                    <button type="button" onClick={() => go('create')} data-testid={TEST_IDS.goCreate}>Create account</button>
                    <button type="button" onClick={() => go('reset')} data-testid={TEST_IDS.goReset}>Forgot password?</button>
                  </Match>
                  <Match when={mode() !== 'signin'}>
                    <button type="button" onClick={() => go('signin')} data-testid={TEST_IDS.backToSignIn}>Sign in</button>
                  </Match>
                </Switch>
              </div>
            </form>
          </Show>
        </Show>
      </section>
    </main>
  );
}
